import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import DatePicker from "react-datepicker";
import { format } from "date-fns";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST, FORMAT_ADD_DATE } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import FileUploader from "@/components/Fields/FileUploader";
import SelectCompanyFullType from "@/components/Selects/SelectCompanyFullType";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectShip from "@/components/Selects/SelectShip";
import SelectInspector from "@/components/Selects/SelectInspector";
import Loader from "@/components/Layouts/Loader";
import { updateInspectionProcessSchema } from "@/schemas/updateInspectionProcessSchema";
import { getDataVessel } from "@/services/vesselService";
import { getInspectorPersonData } from "@/services/inspectorPersonService";
import { getNominationById, putStatusProcessByInspectingCompany } from "@/services/nominationService";

import '../styles/Form.css';

const UpdateInspectionProcess = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();

    const { id } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataAdd, setDataAdd] = useState(null) as any;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [listShipName, setListShipName] = useState([]) as any;
    const [listInspectorPerson, setListInspectorPerson] = useState([]) as any;
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [isLoading, setIsLoading] = useState(false);
    const [defaultType, setDefaultType] = useState('');

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Submit Inspection Process?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Submit Inspection Process?");
        setConfirmationTest("Are you sure you want to submit?");
        setConfirmationTitleButton("Submit Inspection Process");
        onOpen();
        setDataAdd(data);
        setIsSubmitting(true);
    }

    const generateData = () => {
        const data = new FormData();
        data.append('Id', id || '');
        data.append('SequenceID', dataDetail?.sequenceID || '');
        data.append('InspectionDate', dataAdd.actualInspectionDate ? format(dataAdd.actualInspectionDate, FORMAT_ADD_DATE) : '');
        data.append('InspectorId', dataAdd.inspector || '');
        data.append('Country', dataAdd.country || '');
        data.append('Port', dataAdd.port || '');
        data.append('ResultFile', dataAdd.inspectionResult || '');
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Submit Inspection Process?") {
            navigate('/inspection-process');
            return;
        }

        const obj = generateData();
        const response = await putStatusProcessByInspectingCompany(obj) as any;
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Inspection process submitted.',
                description: "We've submitted inspection process for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/inspection-process')
            }, 1000);
        } else {
            toast({
                title: 'Inspection process failed to submit.',
                description: "We unabled to submit inspection process for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    // const handleDrop = (acceptedFiles: File[], setFieldValue: any, fieldName: string) => {
    //     setFieldValue(fieldName, acceptedFiles[0]);
    // };

    const getListShipName = async () => {
        const response = await getDataVessel() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedOptions = response.data.map((item: any) => ({ value: item.vesselId, label: item.vesselName }));
            setListShipName(formattedOptions);
        } else {
            setListShipName([]);
        }
    }

    const getDataListInspectorPerson = async () => {
        const response = await getInspectorPersonData() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.inspectorPersonId,
                label: item.inspectorPersonName,
            }));
            setListInspectorPerson(formattedData);
        } else {
            toast({
                title: 'Failed to get data inspector person.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListInspectorPerson([]);
        }
    }

    const getDataDetail = async () => {
        const response = await getNominationById(id) as any;
        if (response?.status === HttpStatusCode.Ok) {
            setDataDetail(response.data);

            if (response.data.sire) {
                setDefaultType('SIRE');
            } else if (response.data.bire) {
                setDefaultType('BIRE');
            } else if (response.data.vetting) {
                setDefaultType('Vetting');
            }

            setIsLoading(false);
        } else {
            setIsLoading(false);
            toast({
                title: 'Failed to load nomination data.',
                description: response.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    useEffect(() => {
        getDataListInspectorPerson();
        getListShipName();
    }, [])

    useEffect(() => {
        getDataDetail();
    }, [id])

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                actualInspectionDate: dataDetail ? '' : '',
                                inspector: dataDetail ? dataDetail.inspectorId : '',
                                country: dataDetail ? dataDetail.country : '',
                                port: dataDetail ? dataDetail.portOfInspection : '',
                                vesselName: dataDetail ? dataDetail.vesselId : '',
                                type: dataDetail ? defaultType : '',
                                inspectionResult: null
                            }}
                            validationSchema={updateInspectionProcessSchema}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Field name='actualInspectionDate'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.actualInspectionDate && form.touched.actualInspectionDate} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Actual Inspection Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div style={{
                                                        width: '100%',
                                                        height: '2.5rem',
                                                        fontSize: '1rem',
                                                        paddingInlineStart: '1rem',
                                                        paddingInlineEnd: '1rem',
                                                        borderRadius: '0.375rem',
                                                        minWidth: '0px',
                                                        outline: '2px solid transparent',
                                                        outlineOffset: '2px',
                                                        position: 'relative',
                                                        appearance: 'none',
                                                        transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                        paddingTop: '0.35rem',
                                                        marginBottom: (form.errors.actualInspectionDate && form.touched.actualInspectionDate) ? '1.25rem' : 0,
                                                        border: form.errors.actualInspectionDate && form.touched.actualInspectionDate ? '2px solid red' : '1px solid #e5e7e9'
                                                    }}>
                                                        <DatePicker
                                                            id="actualInspectionDate"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => props.setFieldValue('actualInspectionDate', date)}
                                                            dateFormat="dd-MM-YYYY"
                                                            placeholderText="Actual Inspection Date"
                                                            readOnly={isSubmitting}
                                                        />
                                                        <FormErrorMessage style={{ marginLeft: '-1rem', marginTop: '12px' }}>{form.errors.actualInspectionDate}</FormErrorMessage>
                                                    </div>
                                                </div>
                                                
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspector'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.inspector && form.touched.inspector} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Inspector<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectInspector name='inspector' handleChange={({ value }: any) => props.setFieldValue('inspector', value)} value={props.values.inspector} defaultValue={props.values.inspector} options={listInspectorPerson} hasError={form.errors.inspector && form.touched.inspector} />
                                                        <FormErrorMessage>{form.errors.inspector}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='country'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.country && form.touched.country} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCountry name='country' handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} hasError={form.errors.country && form.touched.country} defaultValue={props.values.country} />
                                                        <FormErrorMessage>{form.errors.country}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='port'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.port && form.touched.port} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Port</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Inspector' className="form-input" />
                                                        <FormErrorMessage>{form.errors.port}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='vesselName'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.vesselName && form.touched.vesselName} style={{ marginBottom: 15, alignItems: 'center' }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">
                                                        Vessel Name
                                                    </FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectShip name='vesselName' placeholder="Choose Vessel" handleChange={({ value }: any) => props.setFieldValue('vesselName', value)} value={props.values.vesselName} options={listShipName} hasError={form.errors.vesselName && form.touched.vesselName} isDisabled={isSubmitting} />
                                                        <FormErrorMessage>{form.errors.vesselName}</FormErrorMessage>
                                                    </div>
                                                </div>
                                                
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='type'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.type && form.touched.type} style={{ marginBottom: 15, alignItems: 'center' }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">
                                                        Type
                                                    </FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCompanyFullType name='type' handleChange={({ value }: any) => props.setFieldValue('type', value)} value={props.values.type} defaultValue={props.values.type} hasError={form.errors.type && form.touched.type} />
                                                        <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                                    </div>
                                                </div>
                                                
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='inspectionResult'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.inspectionResult && form.submitCount} style={{ marginBottom: 15, alignItems: 'center' }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">
                                                        Inspection Result
                                                    </FormLabel>
                                                    <div className="form-input-container">
                                                        <FileUploader
                                                            onDrop={(e) => props.setFieldValue('inspectionResult', e[0])} //handleDrop(e, props.setFieldValue, 'inspectionResult')}
                                                            isInvalid={form.errors.inspectionResult && form.submitCount} isDisabled={props.isSubmitting} type="inspection-process"
                                                        />
                                                        <FormErrorMessage>{form.errors.inspectionResult}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Submit
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>
            }
        </>
    )
}

export default UpdateInspectionProcess;
